import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Modal } from 'react-daisyui';
import { useTranslation, Trans } from 'react-i18next';
import WhatsAppConnectAdvisorHelp from 'src/components/WhatsAppConnectAdvisorHelp/WhatsAppConnectAdvisorHelp.component';
import type { tWhatsAppConnectAdvisorHelpTranslations } from 'src/components/WhatsAppConnectAdvisorHelp/WhatsAppConnectAdvisorHelp.types';
import ConnectionFailedError from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.component';
import ConnectionFailedClosed from 'src/components/WhatsAppConnect/ConnectionFailedClosed/ConnectionFailedClosed.component';
import ConnectionEscalated from 'src/components/WhatsAppConnect/ConnectionEscalated/ConnectionEscalated.component';
import ConnectionActivationFailed from 'src/components/WhatsAppConnect/ConnectionActivationFailed/ConnectionActivationFailed.component';
import { Button } from '@leadsales/design-system/react';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { isAdvisorHelpEligible } from 'src/providers/SubscriptionProvider/Subscription.utils';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import {
  LEGACY_CONNECTION_FLOW,
  WAPI_EVENTS,
} from 'src/hooks/analytics/wapiAnalytics.constants';
import {
  useWhatsAppConnectState,
  type tWapiConnectAnalytics,
} from 'src/hooks/whatsapp/useWhatsAppConnectState.hook';
import { readWapiFailureDetails } from 'src/hooks/whatsapp/whatsappConnect.types';
import { getEmbeddedSignupAttemptId } from 'src/lib/facebook/embeddedSignupTelemetry';
import { WHATSAPP_CLOUD_VS_COEX_HELP_URL } from 'src/constants/whatsapp.constants';
import './WhatsAppBusinessConnectionModal.styles.scss';

const DASHBOARD_TRANSLATION_NAMESPACE = 'whatsappConnect/whatsappConnect';

function buildAdvisorTranslations(
  t: ReturnType<typeof useTranslation>['t'],
): tWhatsAppConnectAdvisorHelpTranslations {
  return {
    liveTitle: t(
      'components.modals.MetaConnection.advisorHelp.live.title',
      '¿Tuviste problemas con Meta?',
    ),
    liveText: t('components.modals.MetaConnection.advisorHelp.live.text'),
    livePrimaryCta: t(
      'components.modals.MetaConnection.advisorHelp.live.primaryCta',
    ),
    liveSecondaryText: t(
      'components.modals.MetaConnection.advisorHelp.live.secondaryText',
    ),
    liveSecondaryCta: t(
      'components.modals.MetaConnection.advisorHelp.live.secondaryCta',
    ),
    offlineTitle: t(
      'components.modals.MetaConnection.advisorHelp.offline.title',
      '¿Tuviste problemas con Meta?',
    ),
    offlineText: t('components.modals.MetaConnection.advisorHelp.offline.text'),
    offlinePrimaryCta: t(
      'components.modals.MetaConnection.advisorHelp.offline.primaryCta',
    ),
    offlineHoursInfo: t(
      'components.modals.MetaConnection.advisorHelp.offline.hoursInfo',
    ),
  };
}

export default function WhatsAppBusinessConnectionModal() {
  const {
    setWhatsAppBusinessConnectionModalIsOpen,
    whatsAppBusinessConnectionModalIsOpen,
    whatsAppBusinessConnectionModalSource,
    whatsAppBusinessConnectionModalIsLegacyChosen,
  } = useModalsContext();
  const { t } = useTranslation('dashboard/dashboard');
  // INT-1217: the report-error instruction lives in the shared namespace, not in
  // dashboard/dashboard, so its quoted Meta label cannot drift between surfaces.
  const { t: tConnect } = useTranslation(DASHBOARD_TRANSLATION_NAMESPACE);
  const { subscription } = useSubscriptionContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { trackWapiEvent: trackWapiEventOfRender } = useWapiAnalytics();
  const { refetchIntegrations } = useIntegrationsContext();

  // `trackWapiEvent` is a new function on every render. Read through a
  // latest-value ref, it keeps the callbacks below stable.
  const trackWapiEventRef = useRef(trackWapiEventOfRender);
  trackWapiEventRef.current = trackWapiEventOfRender;
  const trackWapiEvent = useCallback<typeof trackWapiEventOfRender>(
    (name, payload) =>
      trackWapiEventRef.current(name, {
        ...payload,
        connection_flow: LEGACY_CONNECTION_FLOW,
      }),
    [],
  );

  // ModalPresence keeps this modal mounted for the whole opening, and nothing
  // keys it by the workspace id, so the opening is counted here. The ref, not
  // the effect's dependencies, decides, so a re-render or a new source while
  // open does not count it again; a close clears it for the next opening. An
  // event without a workspace id is dropped by the analytics hook, so the
  // opening is claimed only once the id is known.
  const isOpeningTrackedRef = useRef(false);
  useEffect(() => {
    if (!whatsAppBusinessConnectionModalIsOpen) {
      isOpeningTrackedRef.current = false;
      return;
    }
    if (isOpeningTrackedRef.current || !currentWorkspaceId) return;
    isOpeningTrackedRef.current = true;
    trackWapiEvent(
      WAPI_EVENTS.INTEGRATION_CONNECTION_OPENED,
      whatsAppBusinessConnectionModalSource
        ? { source: whatsAppBusinessConnectionModalSource }
        : {},
    );
  }, [
    whatsAppBusinessConnectionModalIsOpen,
    whatsAppBusinessConnectionModalSource,
    currentWorkspaceId,
    trackWapiEvent,
  ]);

  const showAdvisorHelp = isAdvisorHelpEligible(subscription);
  const advisorTranslations = buildAdvisorTranslations(t);

  const handleAdvisorLive = useCallback(() => {
    trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED);
  }, [trackWapiEvent]);

  const handleAdvisorSchedule = useCallback(() => {
    trackWapiEvent(
      WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED,
    );
  }, [trackWapiEvent]);

  const closeModal = useCallback(() => {
    setWhatsAppBusinessConnectionModalIsOpen(false);
  }, [setWhatsAppBusinessConnectionModalIsOpen]);

  // Memoized so the analytics object identity is stable across renders —
  // otherwise it cascades into the shared hook's useCallback deps and
  // recreates `launchConnection` on every render. Mirrors the pattern in
  // the Onboarding wrapper (`WhatsAppConnect.hook.ts`).
  const wapiAnalytics = useMemo<tWapiConnectAnalytics>(
    () => ({
      onStarted: () =>
        trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_STARTED),
      onSuccess: () =>
        trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_SUCCESS, {
          status: 'success',
        }),
      // INT-1216 — signup_attempt_id is additive: event name, `status` and
      // `reason` are unchanged so existing dashboards keep working. The id is
      // the join key between a popup_closed row and the abandonment step Meta
      // reported for the same attempt.
      onError: (reason, err) =>
        trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_ERROR, {
          status: 'error',
          reason,
          errorMessage: err?.message,
          signup_attempt_id: getEmbeddedSignupAttemptId(),
          ...readWapiFailureDetails(err),
        }),
    }),
    [trackWapiEvent],
  );

  const { state, launchConnection, retry, backToConnect } =
    useWhatsAppConnectState({
      workspaceId: currentWorkspaceId,
      onSuccess: closeModal,
      // Opening this modal over an already-connected integration must NOT
      // auto-trigger the success countdown — the user opened it intentionally
      // (e.g. re-watch the video, talk to an advisor). Onboarding keeps the
      // default true so the mobile flow can complete after Embedded Signup.
      autoSuccessOnAlreadyConnected: false,
      analytics: wapiAnalytics,
      surface: 'dashboard_modal',
    });

  // A person who leaves the wizard asked to connect without the guide, so this
  // opening starts on the connect screen, not on a failure the wizard or the
  // integration row left behind. Declared after the hook so it runs after the
  // hook's mount effects and they cannot pull the phase back. The ref keeps it
  // to once per opening; the flag clears when the modal closes.
  const isLegacyResetDoneRef = useRef(false);
  useEffect(() => {
    if (!whatsAppBusinessConnectionModalIsLegacyChosen) {
      isLegacyResetDoneRef.current = false;
      return;
    }
    if (isLegacyResetDoneRef.current) return;
    isLegacyResetDoneRef.current = true;
    backToConnect();
  }, [whatsAppBusinessConnectionModalIsLegacyChosen, backToConnect]);

  // On success, ask the integrations endpoint so the rest of the dashboard
  // sees the connection even if the workspace socket arrives later than the
  // SDK callback. INT-1516: this used to write CONNECTED into the context
  // itself, which painted any 200, a record Meta had not activated included,
  // as connected. The endpoint's row carries the verdict and the refetch writes
  // both containers. successRefetchedRef gates re-execution if state.phase
  // toggles away from 'success' and back.
  const successRefetchedRef = useRef(false);
  useEffect(() => {
    if (state.phase !== 'success') {
      successRefetchedRef.current = false;
      return;
    }
    if (successRefetchedRef.current) return;
    successRefetchedRef.current = true;
    void refetchIntegrations();
  }, [state.phase, refetchIntegrations]);

  const handleRetry = useCallback(() => {
    trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_RETRY_CLICKED, {
      retry_count: state.retryCount,
    });
    retry();
  }, [retry, state.retryCount, trackWapiEvent]);

  const handleEscalatedMount = useCallback(() => {
    trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_ESCALATED_VIEWED);
  }, [trackWapiEvent]);

  const handleClose = useCallback(() => {
    trackWapiEvent(WAPI_EVENTS.INTEGRATION_CONNECTION_CLOSED_BY_USER, {
      phase: state.phase,
    });
    closeModal();
  }, [closeModal, state.phase, trackWapiEvent]);

  function renderBody() {
    switch (state.phase) {
      case 'success':
        return (
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <CheckCircleIcon className="w-16 h-16 text-success" />
            <h3 className="text-xl font-semibold">
              {t(
                'components.modals.MetaConnection.success.title',
                'Tu WhatsApp Business se conectó',
              )}
            </h3>
            <p className="text-sm text-base-content/60">
              {t(
                'components.modals.MetaConnection.success.subtitle',
                'Cerrando en {{count}}...',
                { count: state.countdown },
              )}
            </p>
          </div>
        );

      case 'failed_error':
        return (
          <ConnectionFailedError
            onRetry={handleRetry}
            onBack={backToConnect}
            errorCode={state.errorCode}
            translationNamespace={DASHBOARD_TRANSLATION_NAMESPACE}
            onAdvisorCtaClick={(kind) =>
              trackWapiEvent(
                kind === 'live'
                  ? WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED
                  : WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED,
              )
            }
          />
        );

      case 'failed_closed':
        return (
          <ConnectionFailedClosed
            onRetry={handleRetry}
            onBack={backToConnect}
            signupStep={state.signupStep}
            signupErrorReport={state.signupErrorReport}
            translationNamespace={DASHBOARD_TRANSLATION_NAMESPACE}
            onAdvisorCtaClick={(kind) =>
              trackWapiEvent(
                kind === 'live'
                  ? WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED
                  : WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED,
              )
            }
          />
        );

      case 'verifying':
        // INT-1516 — a held record. It used to fall through to the connect
        // view below, where it would have sat on a disabled "Conectando..."
        // button with the tutorial video.
        return (
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <ClockIcon className="w-16 h-16 text-info" aria-hidden="true" />
            <h3 className="text-xl font-semibold">
              {t(
                'components.modals.MetaConnection.activationPending.title',
                'Meta está confirmando tu número',
              )}
            </h3>
            <p className="max-w-lg text-sm text-base-content/60">
              {t(
                'components.modals.MetaConnection.activationPending.subtitle',
                'Meta todavía no activa tu número en WhatsApp Business API, así que aún no recibes sus mensajes. Te avisaremos cuando quede activo o si necesitas reconectarlo.',
              )}
            </p>
          </div>
        );

      case 'activation_failed':
        return (
          <ConnectionActivationFailed
            reason={state.activationReason}
            onReconnect={handleRetry}
            onBack={backToConnect}
            translationNamespace={DASHBOARD_TRANSLATION_NAMESPACE}
          />
        );

      case 'escalated':
        return (
          <ConnectionEscalated
            onBack={backToConnect}
            signupStep={state.signupStep}
            signupErrorReport={state.signupErrorReport}
            translationNamespace={DASHBOARD_TRANSLATION_NAMESPACE}
            onMount={handleEscalatedMount}
            advisorTranslations={advisorTranslations}
            onAdvisorCtaClick={(kind) =>
              trackWapiEvent(
                kind === 'live'
                  ? WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_LIVE_CTA_CLICKED
                  : WAPI_EVENTS.INTEGRATION_CONNECTION_ADVISOR_SCHEDULE_CTA_CLICKED,
              )
            }
          />
        );

      case 'connect':
      case 'connecting':
      default:
        return (
          <>
            <iframe
              className="WhatsAppBusinessConnectionModal__Iframe"
              src="https://www.youtube.com/embed/sfyVsn6Avyo"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            {/*
              INT-1217 R4 — the report button is the only lever that raises
              error_code coverage (consent-gated by Meta, no partner override), so
              both launch surfaces ask for the click before opening the popup.
              Read from the shared whatsappConnect namespace so the quoted Meta
              label stays byte-identical with the onboarding surface.
            */}
            <p className="text-sm text-base-content/60">
              {tConnect(
                'reportError.instruction',
                'Si algo falla dentro de la ventana de Meta, da clic en «Reportar error a Leadsales». Así recibimos el detalle del error y podemos ayudarte más rápido.',
              )}
            </p>
            {showAdvisorHelp && (
              <div className="WhatsAppBusinessConnectionModal__AdvisorHelp">
                <WhatsAppConnectAdvisorHelp
                  translations={advisorTranslations}
                  onLiveClick={handleAdvisorLive}
                  onScheduleClick={handleAdvisorSchedule}
                />
              </div>
            )}
          </>
        );
    }
  }

  function renderActions() {
    if (
      state.phase === 'failed_error' ||
      state.phase === 'failed_closed' ||
      state.phase === 'escalated' ||
      state.phase === 'activation_failed' ||
      state.phase === 'success'
    ) {
      // The phase-specific screens render their own footer / call to action.
      return null;
    }

    if (state.phase === 'verifying') {
      // KTD10: the held screen informs; reconnect is offered, not pushed.
      return (
        <Modal.Actions>
          <Button color="tertiary" size="md" type="button" onClick={handleRetry}>
            {t(
              'components.modals.MetaConnection.activationPending.reconnectCta',
              'Reconectar',
            )}
          </Button>
          <Button color="primary" size="md" type="button" onClick={handleClose}>
            {t('components.modals.MetaConnection.activationPending.closeCta', 'Cerrar')}
          </Button>
        </Modal.Actions>
      );
    }

    const isConnecting = state.phase === 'connecting';
    return (
      <Modal.Actions>
        <Button
          color="primary"
          size="md"
          type="button"
          onClick={launchConnection}
          disabled={isConnecting}
        >
          {isConnecting
            ? t('components.modals.MetaConnection.connecting', 'Conectando...')
            : t('components.modals.MetaConnection.whatsAppBusinessButton')}
          {!isConnecting && <ChevronRightIcon className="w-4 h-4" />}
        </Button>
      </Modal.Actions>
    );
  }

  return (
    <Modal
      open={whatsAppBusinessConnectionModalIsOpen}
      responsive={false}
      className="WhatsAppBusinessConnectionModal w-full max-w-5xl"
    >
      <Modal.Header>
        <div className="WhatsAppBusinessConnectionModal__Header">
          <div className="WhatsAppBusinessConnectionModal__Header__Text">
            <h2 className="WhatsAppBusinessConnectionModal__Header__Title">
              {t('components.modals.MetaConnection.whatsAppBusinessTitle')}
            </h2>
            <p className="WhatsAppBusinessConnectionModal__Header__Description">
              <Trans
                i18nKey="dashboard/dashboard:components.modals.MetaConnection.whatsAppBusinessDescription"
                components={{
                  br: <br />,
                  lnk: (
                    <a
                      className="text-primary underline"
                      href={WHATSAPP_CLOUD_VS_COEX_HELP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </p>
          </div>
          <XMarkIcon
            onClick={handleClose}
            className="WhatsAppBusinessConnectionModal__Header__Xicon"
          />
        </div>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      {renderActions()}
    </Modal>
  );
}
